import React, { useState, useRef, useEffect } from "react"
import {
  Modal, Button, Select, message,
} from "antd"
import {
  CloudUploadOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons"
import reiNormal from "../../assets/images/evidence-rie.svg"
import reiThinking from "../../assets/images/rie-thinking.png"
import FileChip from "./shared/FileChip"

const { Option } = Select

const STEP_UPLOAD = 1
const STEP_ANALYZING = 2
const STEP_ASSESSMENT = 3
const STEP_RATING = 5

const SUPPORTED_FORMAT_BADGES = ["PDF", "JPG", "PNG", "GIF"]

const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".gif"]
const ALLOWED_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
]

const isValidFileType = (file) => {
  if (file.type && ALLOWED_MIME_TYPES.includes(file.type.toLowerCase())) return true
  const ext = `.${file.name.split(".").pop().toLowerCase()}`
  return ALLOWED_EXTENSIONS.includes(ext)
}

const RATING_OPTIONS = [
  { value: 1, label: "1 - INITIATING" },
  { value: 2, label: "2 - Developing" },
  { value: 3, label: "3 - Proficient" },
  { value: 4, label: "4 - Well Developed" },
]

// Indicator ratings are 0-indexed; evidence_rating sent to API is indicator.rating + 1
const INDICATOR_RATING_LABELS = [
  "INITIATING",
  "Developing",
  "Proficient",
  "Well Developed",
]
const indicatorToEvidenceRating = (r) => r + 1

const INDICATOR_DEFINITIONS = [
  {
    section: "Program Observability",
    name: "Program Visibility Within the School",
    description: [
      "The school building functions as a living SEL environment. Every major common space — hallways, stairwells, the lobby, classroom walls, and bulletin boards — carries named SEL frameworks translated into student-facing behavioral language. This is not limited to generic motivational posters; it includes skill-specific tools posted in the spaces where students need them: conflict resolution menus near common areas, coping strategy tiles near counseling offices, emotion identification charts in classrooms, and self-regulation breathing techniques in corridors.",
      "Schools at the strongest level use a named, school-wide framework (e.g., CARES — Collaborative, Appreciative, Responsible, Engaged, Self-Determined; RESPECT — Yourself, Others, Your School; S.A.I.L. — Show Respect, Act Responsibly, Include Others, Lead by Example) that gives every student consistent language regardless of which room they are in. These frameworks are displayed in multiple locations simultaneously, not as a one-time installation but as a permanent, maintained environmental feature.",
      "Student voice is embedded into the physical SEL environment: students contribute handwritten cards to RESPECT letter walls, fill out \"Be a Good Friend\" boards, illustrate \"We Are Resilient\" project pages, and draw their own Mood Meter emotion faces. The environment teaches both values and skills simultaneously through tools that students can use independently (SOLVE problem-solving steps, EARS Active Listening acronyms, PAUSE-THINK-ACT-REFLECT cycles).",
      "Recognition systems — Student of the Month boards organized by SEL competency (e.g., Self-Management/Impulse Control, Personal Responsibility), RULER \"Got a Kudo\" tickets posted in hallways, advisor nomination forms — make SEL achievement as visible as academic achievement. Monthly or thematic spotlights (Impulse Control in March, Winter Wellness boards, \"We Have a Dream\" for MLK month) show that the SEL culture actively refreshes rather than staying static.",
    ],
  },
  {
    section: "Program Observability",
    name: "Program Visibility to members outside the immediate school community",
    description: [
      "The school's SEL work is visible and accessible beyond the school walls through multiple, layered channels operating simultaneously. At minimum, families receive written explanations of the SEL assessment tools being used — for example, a DESSA handbook page that explains in plain language what the assessment measures and how the school will use results to support students.",
      "At a stronger level, the school participates in district-level, network-level, or sector-wide SEL events and communications. This includes hosting or co-presenting at SEL showcases (e.g., \"Moving from Intent to Impact: Implementing High Quality Social Emotional Learning\"), contributing to district newsletters or community bulletins that spotlight SEL programming, and participating in network-wide professional learning communities (PLCs) where one school shares a practice highlight that circulates to all network colleagues.",
      "The strongest implementations show media and public community engagement: a television crew films a principal and students discussing SEL work; the school hosts an official visiting delegation from an education ministry; staff panel at a national-level commission (e.g., the Aspen Commission on Social-Emotional and Academic Development); the school participates in community service events (We.org) tied to SEL themes. A school parade or community celebration with a public-facing banner (\"At PS 5 We Lift Each Other Up\") demonstrates that SEL identity extends into the neighborhood.",
      "A weekly practitioner newsletter circulating practice highlights across schools — with specific examples like how one school used advisory to help students create a PSA on first impressions — represents a well-developed communication channel that makes each school's work instructive to the broader network.",
    ],
  },
  {
    section: "Program Observability",
    name: "Communication modeling and follow up on program expectations",
    description: [
      "Clear, publicly posted SEL expectations exist at two levels simultaneously: school-wide behavioral norms that every student and staff member is expected to know, and specific skill-building frameworks that operationalize those norms into teachable steps. These are not aspirational statements — they are displayed as reference tools that students and staff can actively use.",
      "At the school-wide level, all five CASEL competencies are articulated in student-accessible language with sub-skills listed for each: Self-Awareness (identifying emotions, recognizing strengths, self-confidence), Self-Management (impulse control, stress management, self-discipline, goal-setting, organizational skills), Social Awareness (perspective-taking, empathy, appreciating diversity), Relationship Skills (communication, relationship-building, teamwork), and Responsible Decision-Making (solving problems, evaluating, reflecting, ethical responsibility). A classroom or hallway display of the full CASEL framework with sub-skills is a concrete artifact of this expectation.",
      "For structured SEL instruction, schools use purchased or developed grade-level curricula with sequenced lesson components — worksheets, self-reflection scales, narrative texts, and both digital and print versions — aligned to specific competencies (e.g., a Grades 3–5 Character Education unit on Respect with story-based instruction and student self-assessment).",
    ],
  },
  {
    section: "Program Observability",
    name: "Stakeholder knowledge of program expectations and fluency of program language",
    description: [
      "Staff and students share a common SEL vocabulary that shows up in multiple settings simultaneously: in classroom instruction, in hallway displays, in school communications, and in how adults structure learning experiences. Students can name, recognize, and apply SEL concepts because the language is used consistently across classrooms.",
      "The highest-quality evidence is lesson plan documentation showing SEL language integrated directly into academic content. In one school, two teachers documented the same-day delivery: one classroom used sentence frames tied to safety and belonging (\"I feel safe in class when…\", \"I could be my best self when…\") as the entry point into creating a group Preamble about their classroom community. A second classroom on the same day used growth mindset framing as the instructional context for a math lesson — students journaled on whether confidence affects math success before attempting problems, with the teacher explicitly naming that being wrong is part of learning. This cross-subject use of identical SEL language on the same day demonstrates school-wide fluency.",
      "A counselor's open house presentation explicitly framing \"soft skills\" inside \"rigorous instruction\" (student-centered classrooms, peer assessment, academic discourse, inquiry-based learning) signals that school leadership understands SEL competencies as integrated with academic excellence, not separate from it. Students in advisory sessions reference the same skills they see on hallway posters (EARS Active Listening, growth mindset language), indicating that display-level and instruction-level familiarity are mutually reinforcing.",
    ],
  },
  {
    section: "Program Observability",
    name: "Systems to monitor program fidelity",
    description: [
      "SEL implementation is monitored through multiple recurring, named venues — not a single annual check-in but a networked set of structures that catches fidelity concerns through multiple channels. A school at this level can name exactly where SEL is discussed among adults: Grade Team Meetings, weekly Monday and Tuesday after-school PDs, Daily Blast Emails, Weekly Events emails, Attendance meetings, Advisory lessons, and a CTE (Career Technical Education) tracker. Having this list as a formal, documented artifact — a slide that answers \"Where do SEL discussions happen?\" — indicates that the school has deliberately mapped its monitoring infrastructure rather than leaving it implicit.",
      "The school calendar provides corroborating evidence: LIM (Leader In Me or Learning in Motion) Action Teams appear as recurring circled items on multiple Monday slots in the school calendar, scheduled alongside formal Professional Development days and Parent Engagement sessions. Action teams with dedicated calendar time represent the operational mechanism for monitoring and responding to fidelity concerns.",
      "Staff use a shared instructional framework (e.g., the School-Connect EARS Active Listening model) and can demonstrate that students are engaging with the tools — a student photographed reading the EARS handout at a desk shows that the monitoring framework has reached the point of student use.",
    ],
  },
  {
    section: "Program Observability",
    name: "Quality of program fidelity",
    description: [
      "SEL lessons happen on a visible, recurring schedule that is communicated broadly. Implementing staff deliver structured SEL content using a named curriculum framework (School-Connect, RULER, CASEL-aligned) and the school documents this delivery through multiple artifact types — lesson plans, bulletin board displays from completed lessons, and newsletter coverage of SEL events.",
      "The February SEL Newsletter — distributed school-wide, featuring \"Glows and Grows\" (teacher shout-outs praising colleagues who demonstrated strong SEL delivery), coverage of SEL Day programming, and named recognition of specific staff — shows that implementation is tracked, celebrated, and communicated. This kind of newsletter functions simultaneously as a monitoring tool (who is implementing well), a recognition mechanism (shout-outs), and a community communication channel.",
      "A school-wide SEL Day with a dedicated bulletin board and organized programming shows that consistent implementation reaches beyond individual classrooms to whole-school events. A Women's Day flyer tied to SEL themes demonstrates that the curriculum is being used purposefully — connected to relevant, timely events rather than taught in isolation.",
    ],
  },
  {
    section: "Data",
    name: "Quality of data collection",
    description: [
      "The school has selected and actively uses a validated, standardized SEL assessment instrument. The DESSA (Devereux Student Strengths Assessment) is the primary documented tool: schools conduct it twice annually (fall and spring), enter data into the Evo platform, and use results to identify students' lowest-scoring CASEL competency areas. An email from the DESSA advisor to school staff demonstrates that instrument setup is a formal, supported process — the email provides step-by-step instructions for uploading DESSA data into Evo and scheduling a results review meeting.",
      "Beyond the DESSA, a school at this level also documents a K-8 sub-indicator tracking spreadsheet that records individual student T-scores on DESSA sub-domains (e.g., Optimistic Thinking items: \"Have a positive attitude,\" \"Look on the bright side\") — showing that instrument use extends beyond summary ratings to granular skill-level data. June calendars with LIM Action Teams circled alongside DESSA review cycles show that assessment cycles have designated calendar time.",
      "A 7th Grade \"Building Character\" bulletin board shout-out and SEL Buzz newsletter coverage of #SELDay show that assessment findings are connected to public programming — students receiving recognition are implicitly tied to the competency areas the DESSA identifies.",
    ],
  },
  {
    section: "Data",
    name: "Use of data to iterate on program implementation",
    description: [
      "The school conducts a full annual evaluation cycle documented across multiple time points. The strongest evidence is a series of four monthly meeting screenshots (October, November, December, January) from a project management tool (Asana), each recording the AIM 2 meeting agenda and action items as the school progresses through its evaluation cycle: uploading fall DESSA data, conducting walkthroughs, implementing a speaker series, and administering student self-assessments by January.",
      "This sequential documentation — not a single summary, but a month-by-month record — demonstrates that evaluation is genuinely ongoing rather than a one-time report. An email documenting a DESSA Student Reflection troubleshooting process shows that student self-assessment is part of the evaluation design, and an observation request email from a coordinator requesting classroom walkthrough scheduling shows that observation-based data collection is formally scheduled and tracked.",
    ],
  },
  {
    section: "Data",
    name: "Communication of data with stakeholders",
    description: [
      "DESSA results are shared with the school community through visual, accessible formats. Bar charts showing pre/post data for the full student population (e.g., 543 students: 22%→20% Need for Instruction, 8%→11% Strength) are the primary sharing artifact — these appear in multiple schools' evidence, showing they are used at staff meetings, in team agendas, and at Parent-Teacher Conferences (PTCs). A meeting agenda explicitly noting \"DESSA shared at PTCs\" is direct evidence that data reaches families, not just staff.",
      "Cultural relevance in programming — as evidenced by advisory project posters celebrating students' heritage countries (Chile, Brazil) — shows that the school community receiving data is recognized as culturally diverse and SEL programming acknowledges that. Student work from culturally grounded advisory projects may be one of the artifacts shared publicly as evidence of SEL impact.",
    ],
  },
  {
    section: "Program Review",
    name: "Systems and visibility of program leadership",
    description: [
      "The school uses structured, tool-based systems for reviewing implementation at multiple levels: student-facing (DESSA charts displayed for grade-level review), staff-facing (Danielson Framework rubric used for implementation quality assessment), and peer nomination (students nominating advisors for recognition by specific SEL competency through a Google Form).",
      "The Advisor of the Month Google Form is a particularly distinctive artifact: students nominate an advisor by identifying which CASEL competency the advisor demonstrates best, naming the skill and providing a justification. This creates a peer-evaluation loop that uses the school's SEL framework as the review criterion — students are not just rating \"who do you like\" but specifically identifying which competency is being modeled well. This feedback mechanism produces both recognition data and qualitative implementation evidence.",
      "DESSA charts used for grade-level team review show that data review is structured to produce actionable responses — not just stored but brought to meetings for deliberate analysis.",
    ],
  },
  {
    section: "Program Review",
    name: "Intentionality of SEL Leadership Team communication",
    description: [
      "Implementation planning is documented in real time through shared digital tools accessible to the whole team. Asana meeting notes from BYW (spring planning session) include a DESSA debrief, alignment of advisory circles to CASEL competency areas, and Student of the Month planning — all captured as dated, named action items visible to all team members.",
      "A formal team meeting with minutes (A&T meeting, four named participants, Spring DESSA planning) shows that planning involves multiple stakeholders deliberating together rather than a single coordinator distributing decisions. The inclusion of per-session pay logistics in meeting notes indicates that participation structures are supported by the school's formal operations, not volunteer-only.",
    ],
  },
  {
    section: "Program Review",
    name: "Systemization of program review",
    description: [
      "The SEL team produces structured data tools that connect assessment findings to planning decisions at a granular level. A DESSA-by-grade-level table documenting 59 students, 20% needing support, with each row showing the student's advisor name, their lowest CASEL competency, available club resources that address that competency, and recommended instructional strategies is the most complete planning artifact in the dataset. This single document shows a direct pipeline from individual student data to resource allocation to instructional response.",
      "Alongside data-driven planning, the school also surveys staff to ensure professional development is responsive to practitioner needs: a Staff Professional Learning (SE PL) Survey where staff select their top three desired PD topics shows that planning is iterative and responsive rather than top-down. Together, these artifacts demonstrate a review cycle that integrates both student data and staff input into SEL programming decisions.",
    ],
  },
  {
    section: "Program Review",
    name: "Program innovation and fidelity",
    description: [
      "The school makes visible, documented adjustments to SEL programming based on data and student needs. A Kindness Challenge flyer documenting the school's \"first SEL week\" (100+ student participants) shows a programming addition responsive to school culture needs. A Winter Break SEL Challenge shows that programming extends beyond the school day and calendar in response to identified student needs. SMART goal forms show that individual students are supported in setting structured, measurable goals tied to SEL competencies.",
      "A Social Worker SEL Workshop Feedback Survey distributed to staff after a professional development session shows that the school collects feedback on its own programming and uses it to iterate. DESSA pre/post charts provide the data baseline that drives programming adjustments — when fall DESSA shows a high percentage of students needing support in a specific competency area, the school can document what programming change was made in response.",
    ],
  },
  {
    section: "Communication",
    name: "Systematization of communication channels within the school",
    description: [
      "SEL data is communicated to implementing staff in action-oriented formats that immediately connect findings to practice. The Aperture dashboard (293 students: 18% Need for Instruction, 68% Typical, 14% Strength) and a GH50 Check-In survey measuring anxiety and positive outlook at the individual classroom level give staff specific, current data about their students — not just school-wide averages.",
      "An email documenting Restorative Circles scheduling (with a period-by-period implementation schedule) shows that communication channels between the SEL team and implementing staff are not limited to data sharing but also coordinate operational details of program delivery across the school day. The Aperture dashboard format — real-time, visual, accessible to any authorized staff member — represents a channel that makes communication asynchronous and always available, not dependent on a single weekly meeting.",
    ],
  },
  {
    section: "Communication",
    name: "Systematization of communication channels with external supports",
    description: [
      "The SEL team communicates with external experts and stakeholders through formal, structured channels that produce actionable outcomes. The most concrete artifact is a DESSA Results Email from an external expert or central program coordinator sharing fall DESSA data with the school team — and explicitly stating that the data triggered two programming changes: Friday School Connect sessions were updated, and counseling groups were formed based on identified skill gaps. This is not passive data delivery but a communication that closes the loop between external assessment and internal programming response.",
      "A Parent Outreach Script (scripted language for staff to use when calling families about DESSA participation and SEL programming) shows that communication with external stakeholders is systematic and coached — staff are given specific language rather than improvising. A Parent and Family Survey shows that family feedback is formally solicited. An SEL Trends Report email (an intern sharing a final analysis report with the team) demonstrates that external research capacity is connected to internal decision-making.",
    ],
  },
  {
    section: "Communication",
    name: "Systematization of communication of program strengths and challenges",
    description: [
      "SEL competencies are explicitly communicated to implementing staff through visual slide decks that highlight specific skills and their classroom applications. Staff can reference named CASEL competencies in planning and delivery because those competencies are presented in a structured, shareable format (Google Slides presentation) rather than implicitly understood.",
      "A school-wide calendar showing how SEL activities map across the full year gives implementing staff visibility into the program's scope — they can see not just their own advisory lesson but the school-wide SEL Day, PD sessions, and action team meetings on a single calendar view. Challenges are documented when present: principal meeting notes (even if partially illegible) show that SEL implementation is discussed at the leadership level, signaling that challenges as well as achievements reach administrative decision-makers.",
    ],
  },
  {
    section: "Communication",
    name: "Systematization and quality of feedback to implementing staff",
    description: [
      "Feedback on implementation quality is structured, named, and team-based rather than informal. A 3/16/22 meeting agenda explicitly structured as a review session — with Asana task assignments distributed across four team members, Spring DESSA review items, and action items assigned by initials — shows that feedback is operationalized as a regular, documented meeting process with accountability built in.",
      "A \"Where We Are\" slide listing all teachers who have been trained in Restorative Practice by name demonstrates that implementation tracking is connected to individual staff development — the school knows exactly who has received training and can identify gaps. An SEL Evaluations co-presentation (two named co-presenters) documenting evaluation findings to the broader team shows that feedback is not only given top-down but is produced collaboratively and shared as a team product.",
      "SEL Meetings attendance logs (recording attendance by name: Thalia, Joe, Marc) demonstrate that participation in feedback structures is tracked over time, making accountability visible.",
    ],
  },
  {
    section: "Communication",
    name: "Systematization, consistency, and quality of program implementation meetings",
    description: [
      "SEL Leadership Team review meetings are formally scheduled in advance, accompanied by a written agenda circulated before the meeting, and connected to a formal planning tool (Program Matrix). An advance meeting email with attached agenda shows that participants receive structured preparation materials — this is not an ad-hoc check-in but a planned deliberative session.",
      "A submitted Program Matrix documenting 80% Universal Support coverage across all CASEL competencies, with 48/52 coverage ratio and three identified gaps, shows that Leadership Team review meetings produce structured outputs. The Program Matrix is the artifact that a team meeting produces — it represents the synthesis of the team's collective review of what programming covers which competencies for which students (Universal/Targeted/Tertiary).",
      "A PD presentation (\"Experiential Practices of SEL\") prepared and delivered by or for the SEL Leadership Team shows that review meetings are connected to professional learning — the team generates knowledge it then shares with staff.",
    ],
  },
  {
    section: "Communication",
    name: "Capacity and consistency of school around SEL Team providing ongoing professional development",
    description: [
      "The SEL Team delivers professional development on a structured, grade- and topic-organized schedule. An Advisory/Workshop Schedule grid showing which SEL topics are covered by which grades in which months (Suicide Prevention → Stress Management → Social Media → Blood/Resilience, rotated across grade levels) demonstrates that PD content is planned ahead and organized systematically, not assembled ad hoc.",
      "The SEL Team documents the results of PD through post-PD data collection: DESSA pre/post charts shared at team meetings show staff the impact of their advisory work, functioning as PD feedback loops. An Advisor of the Month Google Form (students nominating advisors by competency) is a student-facing PD impact assessment — when students can name and evaluate which competency their advisor demonstrates best, it confirms that PD knowledge has transferred from staff to students.",
    ],
  },
  {
    section: "Leadership",
    name: "Quality of communication integration of program to school's mission and vision",
    description: [
      "School leadership demonstrates deep understanding of SEL by connecting individual student data to individual student goals. DESSA Student SMART Goals sheets — where specific students have documented personal goals in SEL language (\"get along with different types of people,\" \"ask for help more often,\" \"do my homework\") — show that leadership has pushed SEL knowledge from the programmatic level down to the individual student action-planning level.",
      "A Mood Meter displayed in a classroom or advisory space used by leadership or counseling staff shows that leaders actively use SEL tools, not just administer them. A RULER Staff Charter word cloud (displayed as a ribbon at PS 5: successful, appreciated, friendship, hardworking, effective, children first, community, passion, cohesiveness) is an artifact of the entire staff co-creating their shared values — a leadership-facilitated process that builds collective ownership of SEL culture.",
      "A school calendar showing leadership's coordination role (scheduling PD days, Parent Engagement sessions, LIM Action Teams, Clerical Days) demonstrates that SEL is resourced through formal administrative structures, not added on top of existing commitments.",
    ],
  },
  {
    section: "Leadership",
    name: "Distribution of program leadership",
    description: [
      "SEL leadership is genuinely distributed across a named team — not concentrated in one coordinator. A meeting attendance log recording three named staff members (Thalia, Joe, Marc) at recurring SEL Meetings shows that multiple people have standing roles in the program. A formal SEL Team agenda that includes DESSA review, showcase planning, and sustainability rubric review as standing agenda items — all decided collectively — demonstrates that key program decisions are made by the team rather than by a single coordinator.",
      "A DESSA K-8 sub-indicator tracking spreadsheet used across all grade levels — with T-scores tracked for sub-skills like \"Have a positive attitude\" and \"Look on the bright side\" — shows that the data infrastructure of the program is managed collectively, with each grade's advisor contributing data and each team member having access to the full K-8 picture.",
      "An email from a DESSA advisor coordinating with the school team on data review logistics shows that even the relationship with the external assessment vendor is managed as a team function, not as a single-person contact.",
    ],
  },
  {
    section: "Leadership",
    name: "Quality of SEL Leadership Team communication",
    description: [
      "The SEL Team formally presents program learning to the school community through structured professional development sessions that are documented with dates, titles, and presenter information. A Google Slides presentation titled \"Experiential Practices of SEL\" dated January 31, 2022 — with a named presentation to a staff audience — demonstrates that program reporting is formal, planned, and knowledge-transferring, not limited to a brief announcement.",
      "This type of presentation goes beyond data sharing: it brings staff into the conceptual and pedagogical framework that the SEL team uses, building shared understanding of why implementation choices are made, not just what is being done.",
    ],
  },
  {
    section: "Leadership",
    name: "Intentionality of opportunities to recognize success",
    description: [
      "Successes are celebrated through multiple, diverse channels — not a single annual ceremony but a set of regular, structured opportunities. The school can document where success stories are shared: a \"Where do SEL discussions happen?\" slide naming Grade Team Meetings, Monday/Tuesday PDs, Daily Blast Emails, Weekly Events emails, Attendance meetings, Advisory lessons, and a CTE tracker shows that celebrations are embedded in every meeting structure rather than isolated to special events.",
      "BYW Asana meeting notes from an annual planning session that includes a Student of the Month planning item alongside DESSA debrief and competency alignment shows that recognition is treated as a programmatic function with the same planning rigor as data review. A forward-looking SEL agenda item (\"showcase planning\") demonstrates that the team proactively builds celebration events into the calendar rather than reacting to them.",
    ],
  },
  {
    section: "Leadership",
    name: "Resources allocated to program sustainability",
    description: [
      "The school maintains a formal RSP (Resilient Scholars Program) Showcase Checklist — a structured documentation package with named folders for: meeting agendas, SEL artifacts, Program Matrix, Implementation Report, school data across multiple years, and an integrated lesson plan. The existence of this checklist as a formal artifact shows that program sustainability is operationalized as a documentation and review system, not left to informal memory.",
      "Programming artifacts show that the school invests resources in both universal and targeted supports: a LGBTQIA+ Support Group sign-up form demonstrates that targeted programming for specific student populations has been resourced and staffed. A Kindness Challenge (100+ student participants) demonstrates that school-wide SEL events receive organizational and logistical support — they are not low-cost bulletin board activities but structured, staffed programs.",
      "A Winter Break SEL Challenge extending programming beyond the school calendar demonstrates that the school has resources and willingness to support student SEL development beyond contracted school hours. An EARS Active Listening handout — a printed, distributable skill reference — represents consistent investment in instructional materials.",
    ],
  },
  {
    section: "Range",
    name: "Quality of program integration (range)",
    description: [
      "SEL competencies are embedded across all subject areas and school functions, not siloed in advisory. A workshop schedule showing Suicide Prevention paired with ELA class and Social Media literacy paired with college readiness preparation demonstrates that SEL is integrated into content courses — not happening instead of academics but alongside and through them.",
      "Civic engagement programming (\"Taking Action\" worksheet on gun violence and police brutality, Stop Asian Hate infographic, \"Limits to Protest\" event flyer) demonstrates integration of Social Awareness and Responsible Decision-Making competencies into real-world, student-relevant topics. A school-wide Gray Day Memorial (logistics and materials documented) shows that the school organizes whole-school events around SEL themes that involve all staff and students — not just advisory teachers.",
      "A CASEL 5-competency wall display visible throughout the school serves as the integrating framework that connects subject-area work to the common SEL language. A June calendar showing multiple SEL-connected programming items across the month demonstrates that integration is not a one-time unit but a persistent, cross-curricular commitment.",
    ],
  },
  {
    section: "Coordination",
    name: "Quality of program integration (coordination)",
    description: [
      "Staff at all levels can articulate how SEL connects to the school's other programming, activities, and priorities. SEL competency slides — circulated to implementing staff in a shared format — give staff the vocabulary and conceptual frame to explain the connection between what they are doing and why it connects to school goals. A school calendar showing SEL activities alongside academic milestones, community events, and administrative events demonstrates that the program fits into the school's overall operational logic rather than competing with other priorities.",
      "A Gray Day Memorial — where logistics include not just SEL content but physical organization (space, timing, whole-school participation) — shows that the school community experiences SEL as integrated into how the school operates, not as a supplemental add-on.",
    ],
  },
  {
    section: "Program Fit / Reinvention",
    name: "Program innovation and fidelity",
    description: [
      "The most comprehensive adaptation artifact is the BYW Program Matrix: a full cross-institutional mapping document where every CASEL sub-competency is explicitly linked to a named existing school program (RSP Student of the Month, ARISTA honor society, GEMS, IVY Key, Student-Led Conferences, Street Law, SAT Prep). This document demonstrates that the SEL team has done the deliberate analytical work of mapping their program to the school's existing culture and assets — adapting SEL delivery through programs students already know and value rather than creating parallel structures.",
      "An International Women's Day Assembly (poster documenting a formal school-wide event) shows adaptation to the school's specific student population (a school for young women) with culturally and identity-relevant programming. A staff-wide DESSA and Parent Outreach coordination email demonstrates that adaptation extends to family engagement strategies — parents are contacted using scripts that reflect the school's relationships with families, not generic outreach. A PD session titled \"Building Relationships with Students\" shows that professional development is adapted to the specific relational context of the school's student community.",
    ],
  },
]

const FileUploadModal = ({
  visible,
  onClose,
  onSubmit,
  isLoading = false,
  initialFile = null,
  rubricId = null,
}) => {
  const [step, setStep] = useState(STEP_UPLOAD)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [selectedIndicatorId, setSelectedIndicatorId] = useState(null)
  const [selectedRating, setSelectedRating] = useState(null)
  const [aiSuggestedIndicatorNumber, setAiSuggestedIndicatorNumber] = useState(null)
  const [aiSuggestedIndicatorName, setAiSuggestedIndicatorName] = useState(null)
  const [aiSuggestedRating, setAiSuggestedRating] = useState(null)
  const [aiSuggestedRatingLabel, setAiSuggestedRatingLabel] = useState(null)
  const [aiIndicatorReasoning, setAiIndicatorReasoning] = useState(null)
  const [aiRatingReasoning, setAiRatingReasoning] = useState(null)
  const [aiRatingDefinitions, setAiRatingDefinitions] = useState(null)
  const [indicators, setIndicators] = useState([])
  const [fileTypeError, setFileTypeError] = useState(null)
  const [aiCouldNotClassify, setAiCouldNotClassify] = useState(false)
  const [aiCouldNotClassifyReason, setAiCouldNotClassifyReason] = useState(null)
  const [reanalyzingIndicatorNumber, setReanalyzingIndicatorNumber] = useState(null)
  const fileInputRef = useRef(null)
  const dropZoneRef = useRef(null)
  const resetState = () => {
    setStep(STEP_UPLOAD)
    setUploadedFile(null)
    setSelectedIndicatorId(null)
    setSelectedRating(null)
    setAiSuggestedIndicatorNumber(null)
    setAiSuggestedIndicatorName(null)
    setAiSuggestedRating(null)
    setAiSuggestedRatingLabel(null)
    setAiIndicatorReasoning(null)
    setAiRatingReasoning(null)
    setAiRatingDefinitions(null)
    setFileTypeError(null)
    setAiCouldNotClassify(false)
    setAiCouldNotClassifyReason(null)
    setReanalyzingIndicatorNumber(null)
    setIndicators([])
  }

  useEffect(() => {
    if (!visible) {
      resetState()
    }
  }, [visible])
  const findIndicatorByNumber = (number) => {
    const num = parseInt(number, 10)
    if (!num || num < 1 || num > 27) return null
    const byIndex = indicators.find((ind) => ind.index === num)
    if (byIndex) return byIndex
    if (indicators[num - 1]) return indicators[num - 1]
    return indicators[0] || null
  }
  const analyzeFile = (file) => {
    setStep(STEP_ANALYZING)
    setReanalyzingIndicatorNumber(null)

    const formData = new FormData()
    formData.append("file", file)

    APIClient.post(
      "/evidence/analyze_for_chatbot",
      formData,
      (response) => {
        // A missing indicator_number (backend now only omits it alongside not_sel_relevant, never
        // on its own) used to silently default to 1 here — same "AI could not classify" state as
        // an explicit not_sel_relevant, rather than guessing an indicator the file was never
        // actually matched to.
        if (response.not_sel_relevant || !response.indicator_number) {
          setAiCouldNotClassify(true)
          setAiCouldNotClassifyReason(response.reason || null)
          setStep(STEP_ASSESSMENT)
          return
        }

        const indicatorNum = response.indicator_number
        const ratingVal = response.rating || null

        const coreLanguage = window.gon && window.gon.CORE_LANGUAGE
        const resolvedIndicatorName = (coreLanguage && coreLanguage[indicatorNum - 1])
          || response.indicator_name
          || null

        setAiSuggestedIndicatorNumber(indicatorNum)
        setAiSuggestedIndicatorName(resolvedIndicatorName)
        setAiSuggestedRating(ratingVal)
        setAiSuggestedRatingLabel(response.rating_label || null)
        setAiIndicatorReasoning(response.indicator_reasoning || null)
        setAiRatingReasoning(response.rating_reasoning || null)
        setAiRatingDefinitions(response.rating_definitions || null)

        const matchedIndicator = findIndicatorByNumber(indicatorNum)
        if (matchedIndicator) {
          setSelectedIndicatorId(matchedIndicator.id)
        }

        setStep(STEP_ASSESSMENT)
      },
      () => {
        setAiSuggestedIndicatorNumber(1)
        setAiSuggestedIndicatorName(null)
        setAiSuggestedRating(null)
        setAiSuggestedRatingLabel(null)
        setAiIndicatorReasoning(null)
        const matchedIndicator = findIndicatorByNumber(1)
        if (matchedIndicator) {
          setSelectedIndicatorId(matchedIndicator.id)
        }
        setStep(STEP_ASSESSMENT)
      },
    )
  }

  // Re-runs the rating analysis when the user manually picks an indicator different
  // from Rei's suggestion — the original rating suggestion was scored against a
  // different indicator's rating definitions, so it no longer applies.
  const analyzeRatingForIndicator = (indicatorNumber) => {
    setStep(STEP_ANALYZING)
    setReanalyzingIndicatorNumber(indicatorNumber)

    const formData = new FormData()
    formData.append("file", uploadedFile)
    formData.append("indicator_number", indicatorNumber)

    APIClient.post(
      "/evidence/analyze_rating_for_indicator",
      formData,
      (response) => {
        setAiSuggestedRating(response.rating || null)
        setAiSuggestedRatingLabel(response.rating_label || null)
        setAiRatingReasoning(response.rating_reasoning || null)
        setAiRatingDefinitions(response.rating_definitions || null)
        if (response.rating) setSelectedRating(response.rating)
        setStep(STEP_RATING)
      },
      () => {
        setAiSuggestedRating(null)
        setAiSuggestedRatingLabel(null)
        setAiRatingReasoning(null)
        setStep(STEP_RATING)
      },
    )
  }

  // If a file is provided externally, validate then skip Step 1 and go straight to analyzing
  useEffect(() => {
    if (visible && initialFile) {
      if (!isValidFileType(initialFile)) {
        setStep(STEP_UPLOAD)
        setFileTypeError(
          `"${initialFile.name}" is not a supported file type. Please upload a PDF, JPG, PNG, or GIF.`,
        )
        return
      }
      setFileTypeError(null)
      setUploadedFile(initialFile)
      analyzeFile(initialFile)
    }
  }, [visible, initialFile])

  useEffect(() => {
    if (visible && indicators.length === 0) {
      const resolvedRubricId = rubricId || (() => {
        const rubricMatch = window.location.pathname.match(
          /\/sustainability_rubrics\/(\d+)/,
        )
        return rubricMatch ? rubricMatch[1] : null
      })()
      if (resolvedRubricId) {
        APIClient.get(
          `/sustainability_rubrics/${resolvedRubricId}`,
          (response) => {
            if (response && response.indicators) {
              setIndicators(response.indicators)
            }
          },
          () => {},
        )
      }
    }
  }, [visible, rubricId])

  // Once indicators load, pre-select the AI-suggested one (handles race condition
  // where analyzeFile runs before the indicators API response arrives)
  useEffect(() => {
    if (
      indicators.length > 0
      && aiSuggestedIndicatorNumber
      && !selectedIndicatorId
    ) {
      const matched = indicators.find((ind) => ind.index === aiSuggestedIndicatorNumber)
        || indicators[aiSuggestedIndicatorNumber - 1]
      if (matched) setSelectedIndicatorId(matched.id)
    }
  }, [indicators, aiSuggestedIndicatorNumber])

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleFileSelect = (file) => {
    if (!file) return
    if (!isValidFileType(file)) {
      setFileTypeError(
        `"${file.name}" is not a supported file type. Please upload a PDF, JPG, PNG, or GIF.`,
      )
      return
    }
    setFileTypeError(null)
    setUploadedFile(file)
    analyzeFile(file)
  }

  const handleDropZoneClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0]
    if (file) handleFileSelect(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.dataTransfer.files && e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }
  const getSelectedIndicatorName = () => {
    const ind = indicators.find((i) => i.id === selectedIndicatorId)
    if (ind) {
      const coreLanguage = window.gon && window.gon.CORE_LANGUAGE
      if (coreLanguage && coreLanguage[ind.index - 1]) return coreLanguage[ind.index - 1]
    }
    return aiSuggestedIndicatorName
  }

  const getSelectedIndicatorRating = () => {
    const ind = indicators.find((i) => i.id === selectedIndicatorId)
    return ind && ind.rating != null ? ind.rating : null
  }
  const getDisplayIndicatorNumber = () => {
    if (selectedIndicatorId) {
      const ind = indicators.find((i) => i.id === selectedIndicatorId)
      if (ind) return ind.index
    }
    if (aiSuggestedIndicatorNumber) return aiSuggestedIndicatorNumber
    return "?"
  }
  const handleFinalUpload = (indicatorId, rating) => {
    if (!indicatorId || !rating) {
      message.warning("Please select an indicator and rating.")
      return
    }
    const formData = new FormData()
    formData.append("file", uploadedFile)
    formData.append("indicator_id", indicatorId)
    formData.append("evidence_rating", rating)
    const aiReasoning = aiRatingReasoning || aiIndicatorReasoning
    if (aiReasoning) formData.append("rating_reasoning", aiReasoning)
    const analysisResult = {
      indicator_number: getDisplayIndicatorNumber(),
      indicator_name: getSelectedIndicatorName() || aiSuggestedIndicatorName,
      rating_label: aiSuggestedRatingLabel,
      reasoning: aiIndicatorReasoning,
      rating_reasoning: aiRatingReasoning,
      filename: uploadedFile && uploadedFile.name,
    }
    onSubmit(formData, analysisResult)
  }
  const handleAssessmentContinue = () => {
    if (!selectedIndicatorId && indicators.length > 0) {
      message.warning("Please select an indicator before continuing.")
      return
    }
    const selectedIndicator = indicators.find(
      (i) => i.id === selectedIndicatorId,
    )
    const needsRatingAnalysis = selectedIndicator
      && (aiCouldNotClassify
        || !aiSuggestedIndicatorNumber
        || selectedIndicator.index !== aiSuggestedIndicatorNumber)
    if (needsRatingAnalysis) {
      analyzeRatingForIndicator(selectedIndicator.index)
      return
    }
    if (aiSuggestedRating) setSelectedRating(aiSuggestedRating)
    setStep(STEP_RATING)
  }

  const handleUpload = () => {
    if (!selectedRating) {
      message.warning("Please select a rating before uploading.")
      return
    }
    handleFinalUpload(selectedIndicatorId, selectedRating)
  }

  const indicatorOptions = indicators.map((ind) => ({
    value: ind.id,
    label: `Indicator ${ind.index}`,
  }))

  const ReiMascot = ({ showSpeechBubble = false }) => {
    if (showSpeechBubble) {
      return (
        <div className="rei-loading">
          <div className="rei-loading__glow" />
          <img
            src={reiNormal}
            alt="Rei mascot"
            className="rei-loading__mascot"
          />
          <span className="rei-loading__badge">
            <img
              src={reiThinking}
              alt=""
              className="rei-loading__badge-icon"
            />
            Thinking…
          </span>
        </div>
      )
    }
    return (
      <img src={reiNormal} alt="Rei mascot" className="rei-mascot-image" />
    )
  }

  const primaryBtnStyle = {
    background: "#a6192e",
    borderColor: "#a6192e",
    color: "#fff",
    borderRadius: "6px",
    height: "40px",
    padding: "0 20px",
    fontWeight: 600,
  }

  const cancelBtnStyle = {
    background: "#fff",
    borderColor: "#d0d0d0",
    color: "#333",
    borderRadius: "6px",
    height: "40px",
    padding: "0 20px",
  }

  const renderStepUpload = () => (
    <div className="evidence-step evidence-step--upload">
      <div
        ref={dropZoneRef}
        className={`evidence-drop-zone${fileTypeError ? " evidence-drop-zone--error" : ""}`}
        onClick={handleDropZoneClick}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleDropZoneClick()}
      >
        <span className="evidence-drop-zone__icon-wrap">
          <CloudUploadOutlined className="evidence-drop-zone__icon" />
        </span>
        <p className="evidence-drop-zone__text">
          <span className="evidence-drop-zone__text-link">Click to upload</span>
          {" "}or drag and drop
        </p>
        <div className="evidence-drop-zone__formats">
          {SUPPORTED_FORMAT_BADGES.map((format) => (
            <span key={format} className="evidence-drop-zone__format-badge">
              {format}
            </span>
          ))}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFileInputChange}
          accept=".pdf,.jpg,.jpeg,.png,.gif"
        />
      </div>
      {fileTypeError && (
        <div className="evidence-file-error">
          <ExclamationCircleFilled className="evidence-file-error__icon" />
          <div className="evidence-file-error__content">
            <p className="evidence-file-error__title">Unsupported file type</p>
            <p className="evidence-file-error__message">{fileTypeError}</p>
          </div>
        </div>
      )}
    </div>
  )

  const renderStepAnalyzing = () => (
    <div className="evidence-step evidence-step--analyzing">
      <FileChip fileName={uploadedFile && uploadedFile.name} />
      <ReiMascot showSpeechBubble />
      <div className="rei-loading__status">
        <span className="rei-loading__label">
          {reanalyzingIndicatorNumber
            ? `Scoring against Indicator ${reanalyzingIndicatorNumber}`
            : "Analyzing your file"}
        </span>
        <span className="rei-loading__dots">
          <span />
          <span />
          <span />
        </span>
      </div>
      <div className="rei-loading__bar">
        <div className="rei-loading__bar-fill" />
      </div>
    </div>
  )

  const renderStepAssessment = () => (
    <div className="evidence-step evidence-step--assessment">
      <FileChip fileName={uploadedFile && uploadedFile.name} />
      <ReiMascot />
      <div className="rei-assessment-card">
        <div className="rei-assessment-card__title-assessment-main">
          <span className="rei-assessment-card__title-assessment">
            Rei&apos;s Assessment
          </span>
        </div>
        <div className="rei-assessment-card__body-assessment-main">
          {aiCouldNotClassify ? (
            <div style={{ margin: "0 0 14px" }}>
              <p
                style={{
                  margin: "0 0 8px",
                  fontSize: 13,
                  color: "#5a6a7a",
                  lineHeight: 1.5,
                }}
              >
                This file does not appear to be relevant or valid evidence for
                any indicator.
              </p>
              {aiCouldNotClassifyReason && (
                <p
                  style={{
                    margin: "0 0 8px",
                    fontSize: 13,
                    color: "#3a4a5a",
                    background: "#f8f4f0",
                    borderLeft: "3px solid #a6192e",
                    padding: "8px 12px",
                    borderRadius: "0 4px 4px 0",
                    lineHeight: 1.6,
                  }}
                >
                  {aiCouldNotClassifyReason}
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Rei suggestion block */}
              <div style={{ marginBottom: 14 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: aiSuggestedIndicatorName ? 6 : 0,
                  }}
                >
                  <span
                    style={{ fontSize: 12, color: "#8a9bb0", flexShrink: 0 }}
                  >
                    Rei suggests:
                  </span>
                  {aiSuggestedIndicatorNumber ? (
                    <span
                      style={{
                        background: "#f6e8ea",
                        color: "#a6192e",
                        fontWeight: 700,
                        fontSize: 13,
                        padding: "2px 10px",
                        borderRadius: 20,
                        border: "1px solid #e8bcc2",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Indicator {aiSuggestedIndicatorNumber}
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: 13,
                        color: "#8a9bb0",
                        fontStyle: "italic",
                      }}
                    >
                      This file does not appear to be relevant or valid evidence
                      for any indicator.{" "}
                    </span>
                  )}
                </div>
                {aiSuggestedIndicatorNumber && aiSuggestedIndicatorName && (
                  <p
                    style={{
                      margin: 0,
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#a6192e",
                      lineHeight: 1.4,
                    }}
                  >
                    {aiSuggestedIndicatorName}
                  </p>
                )}
              </div>

              {/* Why this indicator */}
              {aiIndicatorReasoning
                && (() => {
                  const bulletLabels = [
                    "What this indicator is about",
                    "What the uploaded file has",
                    "Why this file fits this indicator",
                  ]
                  const parts = aiIndicatorReasoning
                    .split(/\(\d\)/)
                    .map((s) => s.trim())
                    .filter(Boolean)

                  return (
                    <div style={{ margin: "0 0 14px" }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#8a9bb0",
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                          marginBottom: 5,
                        }}
                      >
                        Why this indicator
                      </div>
                      <ul
                        style={{
                          margin: 0,
                          padding: "8px 10px 8px 22px",
                          fontSize: 13,
                          color: "#3a4a5a",
                          background: "#f8f4f0",
                          borderLeft: "3px solid #a6192e",
                          borderRadius: "0 4px 4px 0",
                          lineHeight: 1.6,
                          listStyle: "disc",
                        }}
                      >
                        {parts.map((part, i) => (
                          <li
                            key={i}
                            style={{
                              marginBottom: i < parts.length - 1 ? 8 : 0,
                            }}
                          >
                            <strong>
                              {bulletLabels[i] || `Point ${i + 1}`}
                            </strong>{" "}
                            {part}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })()}
            </>
          )}

          <div style={{ borderTop: "1px solid #ebedef", margin: "0 0 14px" }} />

          {indicatorOptions.length > 0 && (
            <div className="evidence-field-group">
              <label className="evidence-field-label">
                {aiCouldNotClassify ? (
                  "Please choose an indicator from below for this file"
                ) : (
                  <>
                    Do you want to choose this indicator or aother indicator{" "}
                    <span className="evidence-field-label__hint">(1–27)</span>
                  </>
                )}
              </label>
              <Select
                className="evidence-select"
                value={selectedIndicatorId}
                onChange={(val) => setSelectedIndicatorId(val)}
                placeholder="Select an Indicator"
                showSearch
                filterOption={(input, option) => {
                  const children = option && option.children ? option.children : ""
                  return children.toLowerCase().includes(input.toLowerCase())
                }}
              >
                {indicatorOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
              {selectedIndicatorId
                && (() => {
                  const ind = indicators.find(
                    (i) => i.id === selectedIndicatorId,
                  )
                  const def = ind && INDICATOR_DEFINITIONS[ind.index - 1]
                  if (!def) return null
                  return (
                    <div
                      style={{
                        marginTop: 10,
                        padding: "10px 14px",
                        background: "#fef6f7",
                        borderLeft: "3px solid #a6192e",
                        borderRadius: 4,
                        fontSize: 13,
                        color: "#444",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#8a9bb0",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: 4,
                        }}
                      >
                        Indicator {ind.index} · {def.section}
                      </span>
                      <strong
                        style={{
                          color: "#a6192e",
                          display: "block",
                          fontSize: 14,
                          lineHeight: 1.4,
                          marginBottom: 8,
                        }}
                      >
                        {def.name}
                      </strong>
                      <div
                        style={{
                          maxHeight: 220,
                          overflowY: "auto",
                          paddingRight: 6,
                        }}
                      >
                        {def.description.map((paragraph, i) => (
                          <p
                            key={i}
                            style={{
                              margin:
                                i < def.description.length - 1 ? "0 0 8px" : 0,
                              lineHeight: 1.5,
                            }}
                          >
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </div>
                  )
                })()}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderStepRating = () => {
    const existingRating = getSelectedIndicatorRating()
    const existingRatingLabel = existingRating != null ? INDICATOR_RATING_LABELS[existingRating] : null
    const existingRatingValue = existingRating != null ? indicatorToEvidenceRating(existingRating) : null

    return (
      <div className="evidence-step evidence-step--rating">
        <FileChip fileName={uploadedFile && uploadedFile.name} />
        <ReiMascot />
        <div className="rei-assessment-card">
          <div className="rei-assessment-card__title-assessment-main">
            <span className="rei-assessment-card__title-assessment">
              Select Your Rating
            </span>
          </div>
          <div className="rei-assessment-card__body-assessment-main">
            {existingRatingLabel && (
              <p style={{ margin: "0 0 10px", fontSize: 14, color: "#3a4a5a" }}>
                Current score: <strong>{existingRatingLabel}</strong>
              </p>
            )}

            {/* Rei suggestion badge row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 6,
                width: "100%",
              }}
            >
              <span style={{ fontSize: 13, color: "#5a6a7a" }}>
                Rei suggest:
              </span>

              {aiSuggestedRatingLabel ? (
                <span
                  style={{
                    background: "#f6e8ea",
                    color: "#a6192e",
                    fontWeight: 700,
                    fontSize: 13,
                    padding: "2px 10px",
                    borderRadius: 20,
                    border: "1px solid #e8bcc2",
                  }}
                >
                  {aiSuggestedRatingLabel}
                </span>
              ) : (
                <span
                  style={{
                    fontSize: 13,
                    color: "#8a9bb0",
                    fontStyle: "italic",
                  }}
                >
                  This indicator does not have a suggested rating.
                </span>
              )}
            </div>

            {/* Why this rating */}
            {aiRatingReasoning && (
              <div style={{ margin: "0 0 14px" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#8a9bb0",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    marginBottom: 5,
                  }}
                >
                  Why this rating
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: "#3a4a5a",
                    background: "#f8f4f0",
                    borderLeft: "3px solid #a6192e",
                    padding: "8px 12px",
                    borderRadius: "0 4px 4px 0",
                    lineHeight: 1.6,
                  }}
                >
                  {aiRatingReasoning}
                </p>
              </div>
            )}

            {/* Definitions for all 4 rating levels */}
            {aiRatingDefinitions && aiRatingDefinitions.length === 4 && (
              <div style={{ margin: "0 0 16px" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#8a9bb0",
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    marginBottom: 6,
                  }}
                >
                  Rating Definitions
                </div>
                <div>
                  {INDICATOR_RATING_LABELS.map((label, i) => {
                    const isRatingMatch = aiSuggestedRating === i + 1
                    return (
                      <div
                        key={label}
                        style={{
                          marginBottom: i < 3 ? 8 : 0,
                          fontSize: 12,
                          color: isRatingMatch ? "#a6192e" : "#555",
                          background: isRatingMatch ? "#fef6f7" : "#fafafa",
                          border: isRatingMatch
                            ? "1px solid #e8bcc2"
                            : "1px solid #e8e8e8",
                          borderRadius: 4,
                          padding: "10px 12px",
                          lineHeight: 1.6,
                        }}
                      >
                        <strong style={{ display: "block", marginBottom: 3 }}>
                          {i + 1} - {label}
                          {isRatingMatch ? " (Rei's pick)" : ""}
                        </strong>
                        {aiRatingDefinitions[i]}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <div
              style={{ borderTop: "1px solid #ebedef", margin: "0 0 14px" }}
            />

            {existingRatingLabel && aiSuggestedRating
              && aiSuggestedRating === existingRatingValue && (
              <p style={{
                margin: "0 0 14px", fontSize: 13, color: "#3a4a5a", fontStyle: "italic",
              }}
              >
                Already rated <strong>{existingRatingLabel}</strong> — Rei agrees. You can
                still change it manually below.
              </p>
            )}

            {existingRatingLabel
              && !(aiSuggestedRating && aiSuggestedRating === existingRatingValue) && (
              <div style={{
                display: "flex", gap: 8, margin: "0 0 14px", flexWrap: "wrap",
              }}
              >
                <Button
                  style={selectedRating === existingRatingValue
                    ? {
                      ...primaryBtnStyle, height: 32, padding: "0 14px", fontWeight: 500,
                    }
                    : { ...cancelBtnStyle, height: 32, padding: "0 14px" }}
                  onClick={() => setSelectedRating(existingRatingValue)}
                >
                  Keep current score ({existingRatingLabel})
                </Button>
                {aiSuggestedRating && aiSuggestedRating !== existingRatingValue && (
                  <Button
                    style={selectedRating === aiSuggestedRating
                      ? {
                        ...primaryBtnStyle, height: 32, padding: "0 14px", fontWeight: 500,
                      }
                      : { ...cancelBtnStyle, height: 32, padding: "0 14px" }}
                    onClick={() => setSelectedRating(aiSuggestedRating)}
                  >
                    Use Rei&apos;s recommendation ({aiSuggestedRatingLabel})
                  </Button>
                )}
              </div>
            )}

            <div className="evidence-field-group">
              <label className="evidence-field-label">
                {aiSuggestedRatingLabel ? (
                  <>
                    Do you want to choose this rating or another rating{" "}
                    <span className="evidence-field-label__hint">(1 – 4)</span>
                  </>
                ) : (
                  "Please choose a rating from below for this file"
                )}
              </label>
              <Select
                className="evidence-select"
                value={selectedRating}
                onChange={(val) => setSelectedRating(val)}
                placeholder="Select a rating"
              >
                {RATING_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderFooter = () => {
    if (step === STEP_UPLOAD) {
      return [
        <Button key="cancel" onClick={handleClose} style={cancelBtnStyle}>
          Cancel
        </Button>,
      ]
    }
    if (step === STEP_ANALYZING) return null
    if (step === STEP_ASSESSMENT) {
      return [
        <Button key="cancel" onClick={handleClose} style={cancelBtnStyle}>
          Cancel
        </Button>,
        <Button
          key="continue"
          style={primaryBtnStyle}
          onClick={handleAssessmentContinue}
          disabled={!selectedIndicatorId && indicatorOptions.length > 0}
        >
          Continue
        </Button>,
      ]
    }
    if (step === STEP_RATING) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button
            style={cancelBtnStyle}
            onClick={() => setStep(STEP_ASSESSMENT)}
          >
            Back
          </Button>
          <div style={{ display: "flex", gap: 10 }}>
            <Button style={cancelBtnStyle} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              style={primaryBtnStyle}
              loading={isLoading}
              onClick={handleUpload}
              disabled={!selectedRating}
            >
              Upload
            </Button>
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <Modal
      title={(
        <div>
          <div>Attach Evidence</div>
          <div className="evidence-modal-subtitle">
            {step === STEP_ANALYZING
              ? "Sit tight while Rei reviews your file."
              : "Please specify the indicator and rating for this evidence."}
          </div>
        </div>
      )}
      open={visible}
      onCancel={handleClose}
      footer={renderFooter()}
      width={step === STEP_ANALYZING ? 640 : 1000}
      centered
      className="evidence-upload-modal"
      maskClosable={step !== STEP_ANALYZING}
      closable={step !== STEP_ANALYZING}
      bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
    >
      {step === STEP_UPLOAD && renderStepUpload()}
      {step === STEP_ANALYZING && renderStepAnalyzing()}
      {step === STEP_ASSESSMENT && renderStepAssessment()}
      {step === STEP_RATING && renderStepRating()}
    </Modal>
  )
}

export default FileUploadModal
